import moment from 'moment-timezone';
import { v4 as uuidv4 } from 'uuid';
import {
  IBrandMenu,
  ICategoryWithMealPeriod,
  IConstraint,
  IMUItem,
  IRestaurantGroup,
  IRestaurantMealPeriod,
  IRestaurantUnavailableEntity,
  IUnavailableEntity,
  IBrandUnavailableEntity,
  IBrandMenuOrderItem,
  IBrandMenuOrderConstraint,
  IBusinessRule,
  IItemConversionBusinessRules,
  IUtterance,
} from '../types/brandMenu';
import {
  BusinessRuleCategory,
  BusinessRuleOption,
  UnavailabilityReason,
} from '../constants/brandMenu';
import { GenericMap } from './types';
import {
  alwaysAvailableTimePeriod,
  checkItemInCartItemTree,
  getTimePeriod,
  isModGroupDuplicateSelection,
  isModGroupSingleSelection,
  ParsedCategory,
  TopLevelMenuItem,
} from './menu';
import {
  DUMMY_CATEGORY,
  DUMMY_MENU_ITEM,
  DUMMY_MODIFIER_GROUP,
} from '../redux/features/menuUnification/menuUnification.constants';
import {
  convertToMap,
  convertToMapUsingName,
  findMatchingPartFromReverse,
  isSubset,
  snakeToCamelCase,
  convertAllKeysSnakeToCamelCase,
} from '.';
import {
  BrandMenuUnavailabilityConstraints,
  ParsedMenuItem,
  ParsedModifierGroup,
} from '../types/menu';
import {
  ALL_ENTRIES,
  TTS_PROMPT,
  TTS_PROMPT_PREFIX,
  UPSELL,
  UPSELL_PREFIX,
} from '../constants';
import { EntityMenuItem } from '../reducers/messagingSlice';
import { getMenuUnificationFeatureFlag } from './session-storage';
import { TimePeriod } from '../generated-interfaces/graphql';
import { CartItem, CartModifierGroup } from './cart';
import { MOD_SEPARATOR } from './constants';
import logger from './logger';
import { CartItemAdditionTypes } from '../constants/event';
import { fillConstraints } from './menuUnification';
import { UnavailableItemsByName } from '../redux/features/menuUnification/menuUnification.props';
import {
  ALWAYS_OFFER,
  BRACKET_PATTERN,
  CONSTRAINT_ITEM_NOT_ON_CHECK,
  DO_NOT_UPSELL_IN_EXPRESS_MODE,
  GENERIC_CONSTRAINT_OPTIONS,
  DISMISS_READBACK_UTTERANCE_NAME,
  HITL_INTENT_PHRASE_CATEGORY,
} from './menuUnification.constants';
import { IntentCard } from '../types/intent';
import { DEFAULT_INTENT_CARDS } from '../constants/intents';
import { splitPromptPhrases } from './promptPhrases';
import {
  hasSpeakTotalInUpsellCondition,
  parseUpsellConditions,
} from './cart.upsellConditions';

export function filterUnavailableEntityValidity(
  obj: IUnavailableEntity,
  timezone: string = 'America/Los_Angeles'
): boolean {
  const currentDate = moment().tz(timezone);

  const { effective_starting, effective_until } = obj;

  // Check if both dates are empty - valid
  if (!effective_starting && !effective_until) {
    return true;
  }

  // Check if both dates are the same - invalid
  if (
    effective_starting &&
    effective_until &&
    effective_starting === effective_until
  ) {
    return false;
  }

  // Parse dates if they exist
  const startDate = effective_starting
    ? moment.tz(effective_starting, timezone)
    : null;
  const endDate = effective_until ? moment.tz(effective_until, timezone) : null;

  // If only effective_starting is present - valid if current date is after start
  if (startDate && !endDate) {
    return currentDate.isAfter(startDate) || currentDate.isSame(startDate);
  }

  // If only effective_until is present - valid if current date is before end
  if (!startDate && endDate) {
    return currentDate.isBefore(endDate);
  }

  // If both dates are present - valid if current date is between them
  if (startDate && endDate) {
    return currentDate.isBetween(startDate, endDate, null, '[]'); // '[]' includes both endpoints
  }

  return false;
}

export function mergeUnavailableEntitiesByName(
  entries: IUnavailableEntity[],
  acc: GenericMap<IUnavailableEntity[]>,
  timezone: string
) {
  return entries.reduce((acc, entry) => {
    // If entry.name is null or empty (it denotes wild card entry), assign ALL_ENTRIES to it
    let name = entry.name;
    if (!name) {
      name = ALL_ENTRIES;
    }

    // Add the entry to unavailableEntries only if the current date lies within effective_starting & effective_until
    if (filterUnavailableEntityValidity(entry, timezone)) {
      if (acc[name]) {
        acc[name].push(entry);
      } else {
        acc[name] = [entry];
      }
    }

    return acc;
  }, acc);
}

export const getRestaurantGroups = (
  restaurantCode: string,
  brandMenuRestaurantGroup: IRestaurantGroup[]
) => {
  return brandMenuRestaurantGroup
    .filter((restaurantGroup) =>
      restaurantGroup.restaurant_codes.includes(restaurantCode)
    )
    .reduce((acc, restaurantGroup) => {
      acc.push(restaurantGroup.name);
      return acc;
    }, [] as string[]);
};

/**
 * Helper function to filter business rules by category and operation
 */
const filterBusinessRulesByCategoryAndOperation = (
  rules: IBusinessRule[],
  category: BusinessRuleCategory,
  operation: BusinessRuleOption
): IBusinessRule[] => {
  return rules.filter(
    (rule) => rule.category === category && rule.then.operation === operation
  );
};

export const extractItemConversionBusinessRules = (
  businessRules: IBusinessRule[] = [],
  restaurantGroups: IRestaurantGroup[] = [],
  restaurantCode?: string
): IItemConversionBusinessRules => {
  const allowedGroups = new Set<string>(['all_restaurants', 'all']);

  if (restaurantCode && restaurantGroups.length) {
    const matchingGroups = getRestaurantGroups(
      restaurantCode,
      restaurantGroups
    );
    matchingGroups.forEach((group) => allowedGroups.add(group));
  }

  const filteredRules = businessRules.filter((rule) => {
    if (!rule.restaurant_group) {
      return false;
    }

    return allowedGroups.has(rule.restaurant_group);
  });

  return {
    comboConversion: filterBusinessRulesByCategoryAndOperation(
      filteredRules,
      BusinessRuleCategory.ComboConversion,
      BusinessRuleOption.ConvertToCombo
    ),
    aLaCarteConversion: filterBusinessRulesByCategoryAndOperation(
      filteredRules,
      BusinessRuleCategory.ALaCarteConversion,
      BusinessRuleOption.ConvertToALaCarte
    ),
    defaultSizeSelection: filterBusinessRulesByCategoryAndOperation(
      filteredRules,
      BusinessRuleCategory.DefaultSizeSelection,
      BusinessRuleOption.SelectDefaultSize
    ),
    comboSizeTransformation: filterBusinessRulesByCategoryAndOperation(
      filteredRules,
      BusinessRuleCategory.ComboSizeTransformation,
      BusinessRuleOption.TransformComboSize
    ),
  };
};

export const populateItemAvailability = (
  entries: IUnavailableEntity[] = []
) => {
  let shouldAddItem = true;
  let available = true;
  let unavailableUntil = '';

  // check if it is a main item
  const validUnavailableEntries = entries.filter(
    (entry: IUnavailableEntity) => !Object.keys(entry.constraints || {}).length
  );

  if (validUnavailableEntries.length) {
    available = false;
    unavailableUntil = validUnavailableEntries[0].effective_until || '';
  }

  return {
    shouldAddItem,
    available,
    unavailableUntil,
  };
};

const parseROPMenuItem = ({
  brandMenuItems,
  brandMenuConstraints,
  unavailableItemsByName,
  childItem,
  pathToItem,
  uniqueName,
  buildViaAutoCombo = false,
  timezone,
  restaurantMealPeriodsByName,
  currentRestaurantGroups,
  mealPeriodActiveCache,
}: {
  brandMenuItems: Record<string, IMUItem>;
  brandMenuConstraints: Record<string, IConstraint>;
  unavailableItemsByName: Record<string, IUnavailableEntity[]>;
  childItem: string;
  pathToItem: string;
  uniqueName?: string;
  buildViaAutoCombo?: boolean;
  timezone?: string;
  restaurantMealPeriodsByName?: Record<string, IRestaurantMealPeriod>;
  currentRestaurantGroups?: string[];
  mealPeriodActiveCache?: Map<string, boolean>;
}): ParsedMenuItem => {
  const { available: stockAvailable, unavailableUntil } =
    populateItemAvailability(unavailableItemsByName[childItem]);

  // Treat an inactive item-level meal_period the same as out-of-stock: keep
  // the option visible so the agent can see what the AI proposed, but render
  // it unavailable (red + strikethrough). The constraint-level meal_period
  // check still gates the whole modifier group elsewhere.
  const mealPeriodActive = checkMealPeriodAvailabilityAndActive({
    mealPeriod: brandMenuItems[childItem]?.meal_period ?? null,
    timezone,
    restaurantMealPeriodsByName,
    entityName: childItem,
    mealPeriodActiveCache,
  });
  const available = stockAvailable && mealPeriodActive;

  return {
    ...DUMMY_MENU_ITEM,
    id: uniqueName || childItem,
    name: childItem,
    itemId: uniqueName || childItem,
    category: 'modifier',
    categoryId: uuidv4(),
    description: brandMenuItems[childItem]?.description || '',
    available,
    unavailableUntil,
    modifierGroups: convertToMap(
      brandMenuItems[childItem].constraints
        .map((constraintName) =>
          parseModifierGroup({
            brandMenuItems,
            brandMenuConstraints,
            unavailableItemsByName,
            constraintName,
            parentItemName: childItem,
            pathToItem: [
              pathToItem,
              uniqueName || childItem,
              constraintName,
            ].join('__'),
            buildViaAutoCombo,
            timezone,
            restaurantMealPeriodsByName,
            currentRestaurantGroups,
            mealPeriodActiveCache,
          })
        )
        .flat()
    ),
    pathToItem,
  };
};

/**
 * Determines the effective restaurant groups for a constraint by combining
 * constraint.restaurant_groups and meal_period.restaurant_groups:
 *
 * - If both restaurant_groups and meal_period are set:
 *     returns intersect(constraint.restaurant_groups, meal_period.restaurant_groups)
 * - If only restaurant_groups is set:
 *     returns constraint.restaurant_groups
 * - If only meal_period is set:
 *     returns meal_period.restaurant_groups
 * - Otherwise: returns null (no group restriction)
 *
 * When currentRestaurantGroups is provided, the return value indicates whether
 * the constraint applies to the current restaurant:
 * - null  → constraint applies (no restriction, or current restaurant is in the applicable groups)
 * - non-null → constraint does not apply (applicable groups exist but exclude the current restaurant)
 */
export const getConstraintApplicableGroups = (
  constraintRestaurantGroups: string[] | null | undefined,
  constraintMealPeriod: string | null,
  restaurantMealPeriodsByName: Record<string, IRestaurantMealPeriod>,
  currentRestaurantGroups?: string[]
): string[] | null => {
  const hasRestaurantGroups = !!constraintRestaurantGroups?.length;
  const hasMealPeriod = !!constraintMealPeriod;

  let applicableGroups: string[] | null = null;

  if (hasRestaurantGroups && hasMealPeriod) {
    const mealPeriodGroups =
      restaurantMealPeriodsByName[constraintMealPeriod!]?.restaurant_groups;
    if (!mealPeriodGroups?.length) {
      applicableGroups = constraintRestaurantGroups!;
    } else {
      applicableGroups = constraintRestaurantGroups!.filter((g) =>
        mealPeriodGroups.includes(g)
      );
    }
  } else if (hasRestaurantGroups) {
    applicableGroups = constraintRestaurantGroups!;
  } else if (hasMealPeriod) {
    applicableGroups =
      restaurantMealPeriodsByName[constraintMealPeriod!]?.restaurant_groups ??
      null;
  }

  if (currentRestaurantGroups && applicableGroups !== null) {
    return applicableGroups.some((g) => currentRestaurantGroups.includes(g))
      ? null
      : applicableGroups;
  }

  return applicableGroups;
};

/**
 * Checks if a constraint is available and active for a restaurant based on meal period restrictions.
 * @param mealPeriod - The meal period name from the constraint
 * @param timezone - The timezone to use for time period calculations
 * @param restaurantMealPeriodsByName - Map of restaurant meal periods by name
 * @param constraintName - The name of the constraint (for logging purposes)
 * @returns true if the constraint is available and active, false otherwise
 */
export const isRestaurantMealPeriodActive = (
  restaurantMealPeriod: IRestaurantMealPeriod,
  timezone: string
): boolean => {
  const { name, availability, start_date, end_date } = restaurantMealPeriod;

  const formattedAvailability = availability.map((unifiedMenuAvailability) =>
    Object.entries(unifiedMenuAvailability).reduce((acc: any, [key, value]) => {
      acc[snakeToCamelCase(key)] = value;
      return acc;
    }, {} as Object)
  );

  const timePeriod = {
    ...alwaysAvailableTimePeriod,
    description: name,
    availability: formattedAvailability?.length
      ? formattedAvailability
      : alwaysAvailableTimePeriod.availability,
    startDate: start_date,
    endDate: end_date,
  };

  return !getTimePeriod([timePeriod], timezone)?.inactive;
};

export const checkMealPeriodAvailabilityAndActive = ({
  mealPeriod,
  timezone,
  restaurantMealPeriodsByName,
  entityName,
  mealPeriodActiveCache,
}: {
  mealPeriod: string | null;
  timezone?: string;
  restaurantMealPeriodsByName?: Record<string, IRestaurantMealPeriod>;
  /** Constraint or item name the meal period belongs to — used for logging */
  entityName: string;
  mealPeriodActiveCache?: Map<string, boolean>;
}): boolean => {
  // If no meal period is specified, constraint is available and active
  if (!mealPeriod || !timezone || !restaurantMealPeriodsByName) {
    return true;
  }

  const cached = mealPeriodActiveCache?.get(mealPeriod);
  if (cached !== undefined) {
    return cached;
  }

  const restaurantMealPeriod = restaurantMealPeriodsByName[mealPeriod];

  // If meal period doesn't exist for restaurant group, the entity is not available
  if (!restaurantMealPeriod) {
    logger.info(
      `Meal period '${mealPeriod}' of ${entityName} not found for restaurant group.`
    );
    mealPeriodActiveCache?.set(mealPeriod, false);
    return false;
  }

  const active = isRestaurantMealPeriodActive(restaurantMealPeriod, timezone);
  if (!active) {
    logger.info(`Meal period '${mealPeriod}' of ${entityName} is not active`);
  }
  mealPeriodActiveCache?.set(mealPeriod, active);
  return active;
};

/**
 * Whether a constraint applies to the current restaurant's groups (via its
 * own restaurant_groups and/or its meal period's restaurant_groups). Returns
 * true when group data is unavailable — no restriction can be evaluated.
 * Complements checkMealPeriodAvailabilityAndActive (the meal-period check);
 * parseModifierGroup and getTopLevelItems apply both so they always agree on
 * which constraint counts.
 */
export const isConstraintApplicableToRestaurantGroups = ({
  constraint,
  restaurantMealPeriodsByName,
  currentRestaurantGroups,
}: {
  constraint?: IConstraint;
  restaurantMealPeriodsByName?: Record<string, IRestaurantMealPeriod>;
  currentRestaurantGroups?: string[];
}): boolean => {
  if (!currentRestaurantGroups || !restaurantMealPeriodsByName) {
    return true;
  }

  return (
    getConstraintApplicableGroups(
      constraint?.restaurant_groups,
      constraint?.meal_period ?? null,
      restaurantMealPeriodsByName,
      currentRestaurantGroups
    ) === null
  );
};

// Options list alphabetically regardless of the sheet's child_items order.
// `numeric` keeps "2 Pumps" ahead of "10 Pumps".
const sortByName = <T extends { name: string }>(items: T[]): T[] =>
  [...items].sort((a, b) =>
    a.name.localeCompare(b.name, undefined, {
      sensitivity: 'base',
      numeric: true,
    })
  );

// HITL-1720: the 86 entries keyed by lowercased name, built once per
// `unavailableItemsByName` snapshot rather than scanned per lookup. Keys that
// differ only by case are merged, not shadowed.
const lowercaseUnavailableIndex = new WeakMap<
  Record<string, IUnavailableEntity[]>,
  Map<string, IUnavailableEntity[]>
>();

const getLowercaseUnavailableIndex = (
  unavailableItemsByName: Record<string, IUnavailableEntity[]>
): Map<string, IUnavailableEntity[]> => {
  let index = lowercaseUnavailableIndex.get(unavailableItemsByName);
  if (!index) {
    index = new Map();
    Object.entries(unavailableItemsByName).forEach(([name, entries]) => {
      const key = name.toLowerCase();
      index!.set(key, [...(index!.get(key) ?? []), ...entries]);
    });
    lowercaseUnavailableIndex.set(unavailableItemsByName, index);
  }
  return index;
};

// HITL-1720: is a `suppress_if_unavailable:` option 86'd? Mirrors the AI's
// check (PRVA-14690, `find_next_reply_in_cart`) row for row, because HITL must
// suppress exactly the prompts the AI does:
// - a whole-item row on the option, or a row on the parent item scoped to this
//   constraint alone — `{constraint: [option]}`. A multi-constraint row
//   describes one unsold combination, not a dead option, so it does not count.
// - item and option names compare case-insensitively; the constraint key on a
//   scoped row compares exactly, as the AI's scoped map is keyed.
// - `**all**` rows do not count: the AI keys scoped rows on the row's own item
//   name, which never matches a cart item.
// - only 86 rows, not meal periods: the AI reads the 86 list alone.
const isUpsellOptionUnavailable = ({
  unavailableItemsByName,
  option,
  parentItemName,
  constraintName,
}: {
  unavailableItemsByName: Record<string, IUnavailableEntity[]>;
  option: string;
  parentItemName?: string;
  constraintName: string;
}): boolean => {
  const index = getLowercaseUnavailableIndex(unavailableItemsByName);
  const entriesFor = (name: string): IUnavailableEntity[] =>
    index.get(name.toLowerCase()) ?? [];

  if (!populateItemAvailability(entriesFor(option)).available) return true;

  if (!parentItemName) return false;
  return entriesFor(parentItemName).some(({ constraints }) => {
    const keys = Object.keys(constraints || {});
    return (
      keys.length === 1 &&
      keys[0] === constraintName &&
      (constraints?.[constraintName] ?? []).some(
        (name) => name.toLowerCase() === option
      )
    );
  });
};

// PRVA-14987: ANY one named option being out takes the prompt off — a
// `drink_size` naming `medium` and `large` must stop pitching as soon as either
// is gone, since its static prompt still speaks both.
const isAnyUpsellOptionUnavailable = ({
  options,
  ...rest
}: {
  unavailableItemsByName: Record<string, IUnavailableEntity[]>;
  options: string[];
  parentItemName?: string;
  constraintName: string;
}): boolean =>
  options.some((option) => isUpsellOptionUnavailable({ option, ...rest }));

// PRVA-15724: the `unavailable_fill_item:` value is used only when it is one of
// the constraint's own options and not itself a named (possibly 86'd) option.
const validUnavailableFillItem = (
  fillItem: string | undefined,
  childItems: string[],
  namedOptions: string[]
): string | undefined =>
  fillItem &&
  childItems.some((child) => child.toLowerCase() === fillItem) &&
  !namedOptions.includes(fillItem)
    ? fillItem
    : undefined;

export const parseModifierGroup = ({
  brandMenuItems,
  brandMenuConstraints,
  unavailableItemsByName,
  constraintName,
  parentItemName,
  pathToItem,
  buildViaAutoCombo = false,
  timezone,
  restaurantMealPeriodsByName,
  currentRestaurantGroups,
  mealPeriodActiveCache,
}: {
  brandMenuItems: Record<string, IMUItem>;
  brandMenuConstraints: Record<string, IConstraint>;
  unavailableItemsByName: Record<string, IUnavailableEntity[]>;
  constraintName: string;
  // The item this constraint hangs off; scopes `suppress_if_unavailable:` 86 rows.
  parentItemName?: string;
  pathToItem: string;
  buildViaAutoCombo?: boolean;
  timezone?: string;
  restaurantMealPeriodsByName?: Record<string, IRestaurantMealPeriod>;
  currentRestaurantGroups?: string[];
  mealPeriodActiveCache?: Map<string, boolean>;
}): ParsedModifierGroup[] => {
  const constraint = brandMenuConstraints[constraintName] as IConstraint;

  const {
    min,
    max,
    prompt,
    child_items,
    upsell_items,
    upsell_default_item,
    upsell_declined_item,
    default_items,
    is_upsell_prompt,
    conditions,
  } = constraint || {};

  const upsell_decline_items = child_items.filter(
    (item) => !upsell_items.includes(item)
  );
  const flattenedConditions = (conditions ?? [])
    .flatMap((c) => c.split('\n'))
    .map((c) => c.trim());
  const {
    suppress_upsell_conditions,
    do_not_upsell_in_express_mode,
    always_offer,
    upsell_excluded_items,
    suppress_if_unavailable_items,
    unavailable_fill_item: rawUnavailableFillItem,
  } = parseUpsellConditions(flattenedConditions);
  const upsell_options_unavailable = isAnyUpsellOptionUnavailable({
    unavailableItemsByName,
    options: suppress_if_unavailable_items,
    parentItemName,
    constraintName,
  });
  const unavailable_fill_item = validUnavailableFillItem(
    rawUnavailableFillItem,
    child_items,
    suppress_if_unavailable_items
  );
  if (upsell_options_unavailable) {
    logger.debug(
      `[UPSELL_OPTION_86ED] constraint "${constraintName}" on "${parentItemName}" suppressed — ${suppress_if_unavailable_items.join(
        ', '
      )} 86'd`
    );
  }
  if (do_not_upsell_in_express_mode) {
    logger.debug(
      `[EXPRESS_MODE] constraint "${constraintName}" parsed with ${DO_NOT_UPSELL_IN_EXPRESS_MODE} (is_upsell_prompt=${!!is_upsell_prompt})`
    );
  }
  if (always_offer) {
    // Distinguish the two cases: paired with the express tag it overrides it;
    // on its own it changes nothing (nothing was suppressing the constraint),
    // which usually means the sheet is missing the express tag.
    logger.debug(
      do_not_upsell_in_express_mode
        ? `[EXPRESS_MODE] constraint "${constraintName}" parsed with ${ALWAYS_OFFER} — overrides ${DO_NOT_UPSELL_IN_EXPRESS_MODE}, will still be offered (is_upsell_prompt=${!!is_upsell_prompt})`
        : `[EXPRESS_MODE] constraint "${constraintName}" parsed with ${ALWAYS_OFFER} but no ${DO_NOT_UPSELL_IN_EXPRESS_MODE} — no effect (is_upsell_prompt=${!!is_upsell_prompt})`
    );
  }

  // Skip processing if the constraint's meal period is not active
  const isMealPeriodActive = checkMealPeriodAvailabilityAndActive({
    mealPeriod: constraint?.meal_period ?? null,
    timezone,
    restaurantMealPeriodsByName,
    entityName: constraintName,
    mealPeriodActiveCache,
  });
  if (!isMealPeriodActive) {
    return [];
  }

  // Skip processing if the constraint does not apply to the current
  // restaurant's groups
  const isGroupApplicable = isConstraintApplicableToRestaurantGroups({
    constraint,
    restaurantMealPeriodsByName,
    currentRestaurantGroups,
  });
  if (!isGroupApplicable) {
    return [];
  }

  // Handle the case where min and max are equal and greater than 1
  // This indicates that multiple identical modifier groups should be created
  if (min === max && min > 1 && !buildViaAutoCombo) {
    return Array.from({ length: max }, (_, index: number) => {
      const indexSuffix = '_' + (index + 1);
      const itemsToAdd = sortByName(
        child_items.map((childItem: string) => ({
          ...parseROPMenuItem({
            brandMenuItems,
            brandMenuConstraints,
            unavailableItemsByName,
            childItem,
            pathToItem: pathToItem + indexSuffix,
            buildViaAutoCombo,
            timezone,
            restaurantMealPeriodsByName,
            currentRestaurantGroups,
            mealPeriodActiveCache,
          }),
          readbackName: brandMenuItems?.[childItem]?.readback_name || '',
          parentModifierGroupName: constraintName,
          parentModifierGroupDisplayName: constraintName,
          parentModifierGroupId: constraintName,
        }))
      );

      return {
        ...DUMMY_MODIFIER_GROUP,
        id: constraintName + indexSuffix,
        uniqueName: constraintName + indexSuffix,
        name: constraintName,
        menuItems: convertToMap(itemsToAdd),
        posProperties: {},
        minimumSelections: 1,
        maximumSelections: 1,
        defaultSelectedItemIds: default_items,
        description: prompt,
        upsell_default_item,
        upsell_items,
        is_upsell_prompt,
        upsell_decline_items,
        upsell_declined_item,
        suppress_upsell_conditions,
        // NOTE: this branch does not carry `do_not_upsell_in_express_mode` —
        // pre-existing and out of scope for HITL-1512 (adding it would start
        // suppressing a class of constraint that never has been). `always_offer`
        // is carried so the two branches do not drift further; it is inert here
        // until the express flag is carried too.
        always_offer,
        upsell_excluded_items,
        upsell_options_unavailable,
        unavailable_fill_item,
      };
    });
  }

  const itemsToAdd = sortByName(
    child_items.map((childItem: string) => ({
      ...parseROPMenuItem({
        brandMenuItems,
        brandMenuConstraints,
        unavailableItemsByName,
        childItem,
        pathToItem,
        buildViaAutoCombo,
        timezone,
        restaurantMealPeriodsByName,
        currentRestaurantGroups,
        mealPeriodActiveCache,
      }),
      readbackName: brandMenuItems?.[childItem]?.readback_name || '',
      parentModifierGroupName: constraintName,
      parentModifierGroupDisplayName: constraintName,
      parentModifierGroupId: constraintName,
    }))
  );

  const result = {
    ...DUMMY_MODIFIER_GROUP,
    id: constraintName,
    name: constraintName,
    menuItems: convertToMap(itemsToAdd),
    posProperties: {},
    minimumSelections: min,
    maximumSelections: max,
    defaultSelectedItemIds: default_items,
    description: prompt,
    upsell_default_item,
    upsell_items,
    is_upsell_prompt,
    upsell_decline_items,
    upsell_declined_item,
    suppress_upsell_conditions,
    do_not_upsell_in_express_mode,
    always_offer,
    upsell_excluded_items,
    upsell_options_unavailable,
    unavailable_fill_item,
  };

  return [result];
};

export const buildFullMenuUnifiedItem = ({
  item,
  brandMenu,
  restaurantCode,
  unavailableItemsByName,
  buildViaAutoCombo = false,
  timezone,
  restaurantMealPeriodsByName,
  brandMenuItemsByName: precomputedBrandMenuItemsByName,
}: {
  item: TopLevelMenuItem;
  brandMenu: IBrandMenu | undefined;
  restaurantCode: string;
  unavailableItemsByName: Record<
    string,
    Array<IUnavailableEntity | IBrandUnavailableEntity>
  >;
  buildViaAutoCombo?: boolean;
  timezone?: string;
  restaurantMealPeriodsByName?: Record<string, IRestaurantMealPeriod>;
  brandMenuItemsByName?: Record<string, IMUItem>;
}) => {
  let modifierGroups: ParsedModifierGroup[] = [];
  if (brandMenu) {
    const brandMenuItems =
      precomputedBrandMenuItemsByName || convertToMapUsingName(brandMenu.items);
    const brandMenuConstraints = convertToMapUsingName(brandMenu.constraints);
    const currentRestaurantGroups = getRestaurantGroups(
      restaurantCode,
      brandMenu.restaurant_groups
    );

    const itemName = item.name?.includes(UPSELL) ? item.id : item.name;

    // Memoize meal-period active lookups for the duration of this build so we
    // pay the time-period evaluation at most once per distinct meal_period.
    const mealPeriodActiveCache = new Map<string, boolean>();

    const constraints = brandMenuItems?.[itemName]?.constraints;
    constraints?.forEach((constraintName) => {
      const { available } = populateItemAvailability(
        unavailableItemsByName[constraintName]
      );
      if (available) {
        parseModifierGroup({
          brandMenuItems,
          brandMenuConstraints,
          unavailableItemsByName,
          constraintName,
          parentItemName: itemName,
          pathToItem: constraintName,
          buildViaAutoCombo,
          timezone,
          restaurantMealPeriodsByName,
          currentRestaurantGroups,
          mealPeriodActiveCache,
        }).forEach((parsedModGroup) => modifierGroups.push(parsedModGroup));
      }
    });
  }

  return {
    ...DUMMY_MENU_ITEM,
    ...item,
    modifierGroups: convertToMap(modifierGroups),
    itemId: item.name + '-' + item.categoryId,
  };
};

/**
 * Wraps a brand menu item that sits under no top-level constraint (e.g.
 * "12 pack jalapeno poppers", reachable only through upsell / side-choice
 * constraints) in the TopLevelMenuItem shape, so it can be built with
 * buildFullMenuUnifiedItem and added to the cart like any top-level item.
 * Returns undefined when the brand menu has no item by that name.
 */
export const findNonTopLevelMenuItem = (
  itemName: string,
  brandMenuItemsByName: Record<string, IMUItem>
): TopLevelMenuItem | undefined => {
  const brandMenuItem = brandMenuItemsByName[itemName];
  if (!brandMenuItem) return undefined;

  return {
    name: brandMenuItem.name,
    id: brandMenuItem.name,
    category: brandMenuItem.category || '',
    categoryId: '',
    available: true,
    readbackName: brandMenuItem.readback_name || '',
  };
};

/**
 * Finds and builds a substitute item for item_substitution business rules.
 * Looks up the target item first in topLevelMenuItems, then falls back to
 * brandMenuItemsByName for non-top-level items, and builds the full
 * ParsedMenuItem via buildFullMenuUnifiedItem.
 */
export const buildSubstituteItem = ({
  targetItemName,
  topLevelMenuItems,
  brandMenu,
  brandMenuItemsByName,
  restaurantCode,
  unavailableItemsByName,
  timezone,
  restaurantMealPeriodsByName,
}: {
  targetItemName: string;
  topLevelMenuItems: GenericMap<TopLevelMenuItem>;
  brandMenu: IBrandMenu | undefined;
  brandMenuItemsByName: Record<string, IMUItem>;
  restaurantCode: string;
  unavailableItemsByName: Record<
    string,
    Array<IUnavailableEntity | IBrandUnavailableEntity>
  >;
  timezone?: string;
  restaurantMealPeriodsByName?: Record<string, IRestaurantMealPeriod>;
}): {
  parsedItem: ParsedMenuItem;
  topLevelMenuItem?: TopLevelMenuItem;
} | null => {
  // First check top-level items
  let topLevelMatch = Object.values(topLevelMenuItems).find(
    (item) => item.name === targetItemName
  );

  // For front_end_substitute_item business rules, the target item may not be a
  // top-level menu item. Fall back to brandMenuItemsByName to find non-top-level
  // items and build them as full ParsedMenuItems.
  if (!topLevelMatch) {
    topLevelMatch = findNonTopLevelMenuItem(
      targetItemName,
      brandMenuItemsByName
    );

    if (!topLevelMatch) {
      logger.warn({
        message: `Item substitution failed: target item "${targetItemName}" not found in top-level menu items or brand menu items`,
      });
      return null;
    }
  }

  return {
    parsedItem: buildFullMenuUnifiedItem({
      item: topLevelMatch,
      brandMenu,
      restaurantCode,
      unavailableItemsByName,
      buildViaAutoCombo: false,
      timezone,
      restaurantMealPeriodsByName,
      brandMenuItemsByName,
    }),
    topLevelMenuItem: topLevelMatch,
  };
};

export const parseCategoryAndMealPeriods = (
  categoriesWithMealPeriod: ICategoryWithMealPeriod[],
  restaurantMealPeriodsByName: Record<string, IRestaurantMealPeriod>
) => {
  const categoriesWithTimePeriod: ParsedCategory[] = [];
  const alwaysAvailableCategoryWithTimePeriods: ParsedCategory[] = [];
  categoriesWithMealPeriod.forEach((category) => {
    if (!category.mealPeriods.length) {
      alwaysAvailableCategoryWithTimePeriods.push({
        ...DUMMY_CATEGORY,
        itemNames: category.item_names,
        name: category.name,
        id: category.name,
        timePeriods: [alwaysAvailableTimePeriod],
      });
    } else {
      categoriesWithTimePeriod.push({
        ...DUMMY_CATEGORY,
        itemNames: category.item_names,
        name: category.name,
        id: category.name,
        timePeriods: category.mealPeriods.map((mealPeriodName) => {
          const { name, availability, start_date, end_date } =
            restaurantMealPeriodsByName[mealPeriodName] || {};
          const formattedAvailability = availability.map(
            (unifiedMenuAvailability) =>
              Object.entries(unifiedMenuAvailability).reduce(
                (acc: any, [key, value]) => {
                  const updatedKey = snakeToCamelCase(key);
                  acc[updatedKey] = value;
                  return acc;
                },
                {} as Object
              )
          );
          return {
            ...alwaysAvailableTimePeriod,
            description: name,
            availability: formattedAvailability,
            startDate: start_date,
            endDate: end_date,
          };
        }),
      });
    }
  });

  return {
    categoriesWithTimePeriod,
    alwaysAvailableCategoryWithTimePeriods,
  };
};

export const getMealPeriodDetails = (
  timePeriods: TimePeriod[],
  mealPeriod: string,
  timezone?: string
) => {
  const matchingEntry = timePeriods.filter(
    (entry: TimePeriod) => entry.description === mealPeriod
  );

  if (matchingEntry && matchingEntry.length) {
    return getTimePeriod([matchingEntry[0]], timezone || '');
  }

  return null;
};

export const isBMItemConstraintCombinationNotAvailable = ({
  cartItem,
  constraints,
  constraintName,
  itemName,
  constraintsOrder,
}: {
  cartItem: CartItem;
  constraints: BrandMenuUnavailabilityConstraints;
  constraintName: string;
  itemName: string;
  constraintsOrder?: string[];
}): boolean => {
  // Get the selected items for each constraint in the cart item & update the current item to the cartSelectedItems
  // Extract item names from selectedItems values (not keys) because keys may be itemIds
  const cartSelectedItems: Record<string, string[]> = {};
  Object.values(cartItem?.childModifierGroups || {}).forEach(
    (modGroup: CartModifierGroup) => {
      const { name, selectedItems } = modGroup;
      if (constraints[name] && Object.keys(selectedItems || {}).length) {
        // Extract item names from CartItem values, not from keys
        // Keys might be itemIds, but constraints use item names
        cartSelectedItems[name] = Object.values(selectedItems || {})
          .filter((selectedItems) => selectedItems.name)
          .map((item: CartItem) => item.name);
      }
    }
  );
  cartSelectedItems[constraintName] = [itemName];

  // Check if all cartSelectedItems keys are present in constraints
  const cartSelectedItemsKeys = Object.keys(cartSelectedItems);
  const constraintsKeys = Object.keys(constraints || {});
  const allConstraintsInCart = constraintsKeys.every((key) =>
    cartSelectedItemsKeys.includes(key)
  );
  if (!allConstraintsInCart) {
    return false;
  }

  // Check one-directional constraint order
  // Only apply unavailability if a constraint that comes BEFORE the current constraint has selections
  // Prefer using constraintsOrder from brand menu item definition (maintains array order)
  // Fall back to cartItem.modifierGroups key order if constraintsOrder not provided
  // Note: Object.keys() order is only guaranteed for string keys in modern JS engines
  const effectiveConstraintsOrder =
    constraintsOrder || Object.keys(cartItem?.modifierGroups || {});

  if (effectiveConstraintsOrder.length > 0) {
    const currentConstraintIndex =
      effectiveConstraintsOrder.indexOf(constraintName);

    // If constraint is in the order array, check one-directional constraint order
    if (currentConstraintIndex !== -1) {
      // Check if any constraint that comes BEFORE the current one has selections
      const hasPriorConstraintSelection = effectiveConstraintsOrder
        .slice(0, currentConstraintIndex)
        .some((priorConstraint) => {
          // Check if this prior constraint is in the unavailable entity's constraints
          if (!constraints[priorConstraint]) {
            return false;
          }
          // Check if this prior constraint has selections in the cart
          const priorSelectedItems = cartSelectedItems[priorConstraint];
          if (!priorSelectedItems || priorSelectedItems.length === 0) {
            return false;
          }
          // Check if the selected items match the constraint array
          return priorSelectedItems.every((item: string) =>
            (constraints[priorConstraint] || []).includes(item)
          );
        });

      // Only apply unavailability if a prior constraint has selections
      if (!hasPriorConstraintSelection) {
        return false;
      }
    }
    // If constraint is not in order array, fall back to bidirectional behavior (continue to bidirectional check below)
  }

  // For each cartSelectedItems key, check if every selected value is present in the corresponding constraint value array.
  const isCombinationNotSold = Object.keys(cartSelectedItems).every((key) =>
    (cartSelectedItems[key] || []).every((item: string) =>
      (constraints[key] || []).includes(item)
    )
  );
  return isCombinationNotSold;
};

export const isBMItemConstraintNotAvailable = ({
  unavailableItemsByName,
  itemName,
  cartItem,
  constraintName,
  unavailabilityReason,
  brandMenu,
  brandMenuItemsByName: precomputedBrandMenuItemsByName,
}: {
  unavailableItemsByName: UnavailableItemsByName;
  itemName: string;
  cartItem: CartItem;
  constraintName: string;
  unavailabilityReason?: UnavailabilityReason;
  brandMenu?: IBrandMenu;
  brandMenuItemsByName?: Record<string, IMUItem>;
}): boolean => {
  const mainItemName = cartItem?.name;
  const unavailableMain = unavailableItemsByName[mainItemName] || [];
  const unavailableAll = unavailableItemsByName[ALL_ENTRIES] || [];
  const bmUnavailableDetails = [...unavailableMain, ...unavailableAll];

  // Get constraints order from brand menu item definition if available
  // This provides a reliable array-based ordering instead of relying on object key order
  let constraintsOrder: string[] | undefined;
  if (brandMenu || precomputedBrandMenuItemsByName) {
    const brandMenuItems =
      precomputedBrandMenuItemsByName ||
      convertToMapUsingName(brandMenu!.items);
    const itemNameForLookup = mainItemName?.includes(UPSELL)
      ? cartItem.id
      : mainItemName;
    const brandMenuItem = brandMenuItems[itemNameForLookup || ''];
    constraintsOrder = brandMenuItem?.constraints;
  }

  const filteredEntry = bmUnavailableDetails?.find((entry) => {
    const { unavailability_reason, constraints } = entry || {};
    const constraintKeys = Object.keys(constraints || {});

    if (
      (!unavailabilityReason ||
        unavailabilityReason == unavailability_reason) &&
      constraintKeys.length &&
      constraints?.[constraintName]?.includes(itemName)
    ) {
      if (constraintKeys.length === 1) {
        return true;
      } else {
        // Check if all constraint keys are present in cartItem.modifierGroups
        const constraintKeys = Object.keys(constraints || {});
        const modGroupNames = Object.keys(cartItem?.modifierGroups || {});
        const allKeysPresent = constraintKeys.every((key) =>
          modGroupNames.includes(key)
        );
        if (allKeysPresent) {
          return isBMItemConstraintCombinationNotAvailable({
            cartItem,
            constraints,
            constraintName,
            itemName,
            constraintsOrder,
          });
        }
      }
    }
  });

  return filteredEntry ? true : false;
};

export const isBMItemNotSold = (
  unavailableItemsByName: UnavailableItemsByName,
  itemName: string
): boolean => {
  const bmUnavailableDetail = unavailableItemsByName[itemName]?.[0];
  return (
    !!bmUnavailableDetail &&
    Object.keys(bmUnavailableDetail.constraints || {}).length === 0 &&
    bmUnavailableDetail.unavailability_reason === UnavailabilityReason.notSold
  );
};

export const isBMItemOutOfStock = (
  unavailableItemsByName: UnavailableItemsByName,
  itemName: string
): boolean => {
  const bmUnavailableDetail = unavailableItemsByName[itemName]?.[0];
  return (
    !!bmUnavailableDetail &&
    Object.keys(bmUnavailableDetail.constraints || {}).length === 0 &&
    bmUnavailableDetail.unavailability_reason ===
      UnavailabilityReason.outOfStock
  );
};

export const getTopLevelItems = (
  availableCategories: ParsedCategory[],
  itemsByName: GenericMap<IMUItem>,
  unavailableItemsByName: {
    [p: string]: Array<
      | IBrandUnavailableEntity
      | IRestaurantUnavailableEntity
      | IUnavailableEntity
    >;
  },
  constraintsByName: GenericMap<IConstraint>,
  timezone?: string,
  restaurantMealPeriodsByName?: Record<string, IRestaurantMealPeriod>,
  currentRestaurantGroups?: string[]
) => {
  let topLevelMenuItems: GenericMap<TopLevelMenuItem> = {};
  // Memoize meal-period active lookups for the duration of this build so we
  // pay the time-period evaluation at most once per distinct meal_period.
  const mealPeriodActiveCache = new Map<string, boolean>();
  availableCategories.forEach((category) => {
    const { name: categoryName } = category;
    category.itemNames?.forEach((itemName) => {
      const item = itemsByName[itemName];
      if (item) {
        const { shouldAddItem, available, unavailableUntil } =
          populateItemAvailability(unavailableItemsByName[itemName]);

        const isNotSold = isBMItemNotSold(unavailableItemsByName, itemName);
        if (shouldAddItem && !isNotSold) {
          const itemName = item.name;
          let topLevelMenuItemName = itemName;
          let slashCommands;
          let speakTotalInUpsell;
          let activeConstraintName: string | undefined;
          if (
            categoryName.includes(TTS_PROMPT) ||
            categoryName.includes(TTS_PROMPT_PREFIX) ||
            categoryName.includes(UPSELL) ||
            categoryName.includes(UPSELL_PREFIX)
          ) {
            //TODO: no need to parse the TTS prompt item name to separate slash commands from name once the BM change is done
            const intentAndSlashCommands = itemName
              .split(':')
              .filter((result) => result.length);
            topLevelMenuItemName = intentAndSlashCommands[0];

            // Use the constraint prompt to get the TTS phrases for slash commands: TTS prompt and Upsell prompt.
            // A slash command item can carry one constraint per meal period /
            // restaurant group — pick the first constraint whose meal period
            // is currently active and which applies to this restaurant's
            // groups (the same two checks parseModifierGroup uses to gate
            // modifier groups), falling back to the first one.
            activeConstraintName =
              item.constraints.find((constraintName) => {
                const constraint = constraintsByName[constraintName];
                return (
                  checkMealPeriodAvailabilityAndActive({
                    mealPeriod: constraint?.meal_period ?? null,
                    timezone,
                    restaurantMealPeriodsByName,
                    entityName: constraintName,
                    mealPeriodActiveCache,
                  }) &&
                  isConstraintApplicableToRestaurantGroups({
                    constraint,
                    restaurantMealPeriodsByName,
                    currentRestaurantGroups,
                  })
                );
              }) ?? item.constraints[0];
            const constraintPrompt =
              item.constraints.length && activeConstraintName
                ? constraintsByName[activeConstraintName]?.prompt
                : '';
            slashCommands = constraintPrompt?.length
              ? splitPromptPhrases(constraintPrompt)
              : undefined;
            // HITL-1570: the constraint may declare that its prompt speaks
            // the check total, which gates the donation upsell on a total
            // being available.
            speakTotalInUpsell = hasSpeakTotalInUpsellCondition(
              activeConstraintName
                ? constraintsByName[activeConstraintName]?.conditions
                : undefined
            );
          }
          if (
            !Object.keys(topLevelMenuItems).includes(itemName) ||
            topLevelMenuItems[itemName].inactive
          ) {
            // Only push the item if it doesn't exit or the current item's category is inactive
            const itemHasMealCategory = item.meal_period
              ? getMealPeriodDetails(
                  category.timePeriods,
                  item.meal_period,
                  timezone
                )
              : null;

            topLevelMenuItems[topLevelMenuItemName] = {
              synonyms: item.customer_synonyms,
              category: category.name,
              categoryId: category.name,
              id: itemName,
              name: topLevelMenuItemName,
              description: item.description,
              readbackName: item?.readback_name || '',
              available,
              unavailableUntil,
              inactive: itemHasMealCategory
                ? itemHasMealCategory?.['inactive']
                : category.inactive,
              activeTimePeriod: itemHasMealCategory
                ? itemHasMealCategory?.['activeTimePeriod']
                : category.activeTimePeriod,
              ...(unavailableItemsByName[itemName]
                ? {
                    unavailabilityConstraints:
                      unavailableItemsByName[itemName]?.[0]?.constraints,
                  }
                : null),
              slashCommands,
              slashCommandIdx:
                slashCommands && slashCommands.length > 0 ? 0 : undefined,
              speakTotalInUpsell,
              activeConstraintName,
            };
          }
        }
      }
    });
  });
  return topLevelMenuItems;
};

export function handleDuplicateModifierGroupIds(
  array: EntityMenuItem[],
  brandMenuConstraints: Record<string, any>
) {
  const seenIds: Record<string, number> = {};
  const result: EntityMenuItem[] = [];

  // Single loop through the entire array
  for (let i = 0; i < array.length; i++) {
    const item = array[i];
    const id = item.modifier_group_id;

    const isSingleChoice = brandMenuConstraints?.[id]
      ? isModGroupSingleSelection(brandMenuConstraints[id])
      : true;
    const isDuplicateChoice = brandMenuConstraints?.[id]
      ? isModGroupDuplicateSelection(brandMenuConstraints[id])
      : false;

    if (isSingleChoice || isDuplicateChoice) {
      // Count remaining occurrences of this ID in the rest of the array
      let remainingCount = 0;
      for (let j = i + 1; j < array.length; j++) {
        if (array[j].modifier_group_id === id) {
          remainingCount++;
        }
      }

      // If we've seen this ID before OR it appears later, it's a duplicate
      const isFirstOccurrence = seenIds[id] === undefined;
      const hasDuplicates = seenIds[id] !== undefined || remainingCount > 0;

      if (hasDuplicates) {
        // Initialize counter if first occurrence
        if (isFirstOccurrence) {
          seenIds[id] = 0;
        }

        // Increment and apply suffix
        seenIds[id]++;

        const newItem: EntityMenuItem = {
          ...item,
          modifier_group_id: `${id}_${seenIds[id]}`,
        };

        result.push(newItem);
      } else {
        // Unique ID, no suffix needed
        result.push(item);
      }
    } else {
      // Modifier allows multiple selection choices - i.e., multiSelect
      result.push(item);
    }
  }

  return result;
}

/**
 * The AI's info-question sentinel constraint: "what's on it?" is represented
 * as a `system_question_about` pseudo-constraint holding `included_with_item_*`
 * pseudo-children. Conversation state, never order content (HITL-1675).
 */
export const SYSTEM_QUESTION_CONSTRAINT = 'system_question_about';

/**
 * A slash-prefixed item name that is NOT an /upsell carrier is a TTS-prompt
 * carrier (e.g. `/pepsi:` hydrated from the AI's `not_on_menu_*` markers):
 * spoken content, never order content (HITL-1675). /upsell carriers are
 * exempt — they are real cart content with their own handling (promotion +
 * HITL-1652 decline rules). Shared by the import-boundary filter and the
 * order-build guard so the two layers cannot drift apart.
 */
export const isPromptCarrierName = (name?: string): boolean => {
  const itemName = name ?? '';
  return itemName.startsWith('/') && !itemName.includes(UPSELL);
};

/**
 * Strips AI-only content from a hypothesis order item BEFORE it enters the
 * cart (HITL-1675). Two artifacts ride along in the AI's hydrated cart:
 *
 * - Slash TTS-prompt carriers (e.g. `/pepsi:` hydrated from the AI's
 *   `not_on_menu_*` markers): whole item is dropped — `null`. `/upsell`
 *   carriers pass through untouched; they are real cart content with their
 *   own handling (promotion + HITL-1652 decline rules).
 * - `system_question_about` sentinel children: removed from `children`
 *   (recursively), keeping the item and its real modifiers.
 *
 * Import-time filtering is the primary cleanup — the agent never sees the
 * ghost card and `agent_cart` stays clean at the source.
 * `buildUnifiedMenuModuleOrder` keeps its own guard as the last line for any
 * entry path this boundary does not cover.
 */
export const sanitizeHypothesisOrderItem = (
  orderItem: EntityMenuItem
): EntityMenuItem | null => {
  if (isPromptCarrierName(orderItem.name)) {
    return null;
  }

  const children = (orderItem.children ?? [])
    .filter((child) => child?.modifier_group_id !== SYSTEM_QUESTION_CONSTRAINT)
    .map((child) => sanitizeHypothesisOrderItem(child))
    .filter((child): child is EntityMenuItem => child !== null);

  return { ...orderItem, children };
};

export const convertBMOrderItemToEntityItem = (
  brandMenuOrderItems: IBrandMenuOrderItem[],
  constraint: string,
  brandMenuConstraints: Record<string, any>
): EntityMenuItem[] =>
  brandMenuOrderItems.map((bmOrderItem) => {
    const bmOrderItemChildren: EntityMenuItem[] = [];

    bmOrderItem.constraints?.forEach((constraint) => {
      const result =
        convertBMOrderItemToEntityItem(
          constraint.items,
          constraint.constraint_name,
          brandMenuConstraints
        ) || [];

      if (result?.length > 1) {
        result.forEach((entry, index) => {
          const isSingleChoice = brandMenuConstraints?.[entry.modifier_group_id]
            ? isModGroupSingleSelection(
                brandMenuConstraints[entry.modifier_group_id]
              )
            : true;

          if (isSingleChoice) {
            const suffix = index + 1;
            bmOrderItemChildren.push({
              ...entry,
              modifier_group_id:
                entry.modifier_group_id +
                (entry.modifier_group_id ? '_' + suffix : ''),
            });
          } else {
            bmOrderItemChildren.push({
              ...entry,
            });
          }
        });
      } else {
        bmOrderItemChildren.push(...result);
      }
    });

    // Index modifier_group_id if it has duplicates
    const modifiedBmOrderItemChildren =
      bmOrderItemChildren?.length >= 1
        ? handleDuplicateModifierGroupIds(
            bmOrderItemChildren,
            brandMenuConstraints
          )
        : bmOrderItemChildren;

    return {
      id: -1, //Hardcode as brand menu does not have id
      name: bmOrderItem.item,
      price: -1, //Hardcode as brand menu does not have price
      quantity: bmOrderItem.quantity,
      pos_specific_properties: {
        pos_id: -1, //Hardcode as brand menu cart does not have pos_specific_properties
      },
      children: modifiedBmOrderItemChildren,
      modifier_group_id: constraint,
      path_to_entity: [], //Hardcode as brand menu cart does not have path_to_entity
      readback: bmOrderItem.readback || false,
      readbackName: bmOrderItem?.readback_name || '',
    };
  });

export const isBMItemAnUnavailableConstraint = (
  childModifierGroups: Record<string, CartModifierGroup>,
  unavailableItemsByName: Record<string, IUnavailableEntity[]>,
  entryName: string,
  modGroupName: string,
  itemName: string
) => {
  let isUnavailable = false;
  const childModGroups = Object.values(childModifierGroups);

  if (getMenuUnificationFeatureFlag()) {
    const selectedConstraints: Record<string, string[]> = {};
    childModGroups.forEach((entry: CartModifierGroup) => {
      const { name, selectedItems } = entry;
      selectedConstraints[name] = Object.keys(selectedItems);
    });
    if (selectedConstraints[modGroupName]) {
      selectedConstraints[modGroupName].push(itemName);
    } else {
      selectedConstraints[modGroupName] = [itemName];
    }

    if (Object.keys(selectedConstraints).length) {
      unavailableItemsByName?.[entryName]?.some((entry: any) => {
        const unavailableEntryConstraints = entry.constraints;
        if (isSubset(unavailableEntryConstraints, selectedConstraints)) {
          isUnavailable = true;
          return true;
        }
        return false;
      });
    }
  }

  return isUnavailable;
};

export const isBMModifierAnUnavailableConstraint = (
  cartItem: CartItem,
  unavailableItemsByName: Record<string, IUnavailableEntity[]>,
  parentModifier: string,
  pathToItem: string,
  modGroupName: string,
  itemName: string
) => {
  let childModGroups = cartItem.childModifierGroups;
  let entryName = cartItem.name;

  if (parentModifier) {
    const parentModifierPath = findMatchingPartFromReverse(
      parentModifier,
      pathToItem,
      MOD_SEPARATOR
    );
    const parentModifierItem = checkItemInCartItemTree({
      cartItem,
      pathToItem: parentModifierPath,
      fromSelectModifier: true,
    });
    if (parentModifierItem) {
      childModGroups = parentModifierItem.childModifierGroups;
      entryName = parentModifier;
    }
  }

  return isBMItemConstraintNotAvailable({
    unavailableItemsByName,
    itemName,
    cartItem: {
      ...cartItem,
      name: entryName,
      childModifierGroups: childModGroups,
    },
    constraintName: modGroupName,
  });
};

export function findOrderIndex(
  path: string[],
  order: IBrandMenuOrderItem[]
): number {
  // Path should have at least 1 element (the main item)
  if (path.length < 1) {
    return -1;
  }

  const [targetItem, ...remainingPath] = path;

  // Find the index of the order item that matches the path
  const index = order.findIndex((orderItem) => {
    // Check if the main item matches
    if (orderItem.item !== targetItem) {
      return false;
    }

    // If path only has the main item, we found a match
    if (remainingPath.length === 0) {
      return true;
    }

    // Check nested structure based on path length
    return matchesNestedPath(remainingPath, orderItem?.constraints);
  });

  return index;
}

export function matchesNestedPath(
  path: string[],
  constraints: IBrandMenuOrderConstraint[] = []
): boolean {
  if (path.length < 2) {
    return false; // Need at least constraint_name and constraint_item
  }

  const [constraintName, constraintItem, ...remainingPath] = path;

  // Find the matching constraint
  const matchingConstraint = constraints.find(
    (constraint) => constraint.constraint_name === constraintName
  );

  if (!matchingConstraint) {
    return false;
  }

  // Find the matching constraint item
  const matchingConstraintItem = matchingConstraint.items.find(
    (item) => item.item === constraintItem
  );

  if (!matchingConstraintItem) {
    return false;
  }

  // If no more path elements, we found a match
  if (remainingPath.length === 0) {
    return true;
  }

  // If there are more path elements, check nested constraints
  if (!matchingConstraintItem.constraints) {
    return false; // No nested constraints available
  }

  // Recursively check the nested structure
  return matchesNestedPath(remainingPath, matchingConstraintItem.constraints);
}

export const convertToMenuModuleOrder = (
  cartItem: CartItem,
  cartItemsQuantity: Record<string, number>,
  isModifier: boolean = false,
  forReadback: boolean = false
) => {
  return {
    item: cartItem.name,
    // For brand menu auto combined items, cartItem.quantity will be populated with value
    quantity: isModifier
      ? cartItem.quantity || 1
      : cartItemsQuantity[cartItem.cartItemId] || 1,
    constraints: fillConstraints(cartItem, cartItemsQuantity, forReadback),
    added_by: cartItem.addedBy || CartItemAdditionTypes.human,
    ...(forReadback ? { readback: cartItem.readback || false } : null),
    ...(forReadback ? { readback_name: cartItem.readbackName || '' } : null),
  } as IBrandMenuOrderItem;
};

// convert time string to minutes since midnight
export const parseTimeToMinutes = (timeStr: string): number => {
  const timeMoment = moment(timeStr, ['h:mma', 'h:mm a', 'HH:mm'], true);
  if (!timeMoment.isValid()) {
    return 0;
  }
  return timeMoment.hours() * 60 + timeMoment.minutes();
};

export const getGreetingDetails = (
  bmWelcomeItems: Record<string, IMUItem>,
  restaurantMealPeriodsByName: GenericMap<IRestaurantMealPeriod>,
  timezone: string,
  bmWelcomeConstraints: IConstraint[]
) => {
  const applicableGreetingItems: GenericMap<
    IMUItem & { activeTimePeriod: any }
  > = {};
  Object.values(bmWelcomeItems).forEach((item: IMUItem) => {
    const { meal_period = '' } = item;
    if (meal_period) {
      const restaurantMealPeriod = restaurantMealPeriodsByName[meal_period];
      if (restaurantMealPeriod) {
        //convert all the recursive keys of restaurantMealPeriod object from snake_case to camelCase
        const timePeriodCategory = getTimePeriod(
          [convertAllKeysSnakeToCamelCase(restaurantMealPeriod)],
          timezone
        );
        if (timePeriodCategory && !timePeriodCategory.inactive) {
          applicableGreetingItems[item.name] = {
            ...item,
            activeTimePeriod: timePeriodCategory.activeTimePeriod,
          };
        }
      }
    }
  });

  // sort applicableGreetingItems based on activeTimePeriod duration (end - start)
  const sortedGreetingItemsArray: {
    name: string;
    item: any;
    duration: number;
  }[] = Object.entries(applicableGreetingItems)
    .map(([name, item]: [string, any]) => {
      const { activeTimePeriod } = item;
      if (
        !activeTimePeriod ||
        !activeTimePeriod.start ||
        !activeTimePeriod.end
      ) {
        return { name, item, duration: Infinity }; // Put items without valid time period at the end
      }

      const startMinutes = parseTimeToMinutes(activeTimePeriod.start);
      const endMinutes = parseTimeToMinutes(activeTimePeriod.end);

      // Handle case where end time is before start time (crosses midnight)
      let duration = endMinutes - startMinutes;
      if (duration < 0) {
        duration += 24 * 60; // Add 24 hours in minutes
      }

      return { name, item, duration };
    })
    .sort((a, b) => a.duration - b.duration); // Sort ascending by duration (shortest first)

  const greetingItem = sortedGreetingItemsArray?.[0] || {};
  const constraintName = greetingItem?.item?.constraints?.[0];
  const greetingConstraint = bmWelcomeConstraints?.find(
    ({ name }) => name === constraintName
  );

  return {
    item: greetingItem.item,
    constraint: greetingConstraint,
  };
};

/**
 * Parse OR conditions from CONSTRAINT_ITEM_NOT_ON_CHECK value.
 * Example: 'entrees[burger]|top_level[burger combo]' ->
 *   [{ constraintName: 'entrees', items: ['burger'] },
 *    { constraintName: 'top_level', items: ['burger combo'] }]
 */
export function parseItemNotOnCheckConditions(
  conditionValue: string
): Array<{ constraintName: string; items: string[] }> {
  const result: Array<{ constraintName: string; items: string[] }> = [];

  if (!conditionValue) return result;

  const candidates = conditionValue.split('|');

  for (let raw of candidates) {
    const candidate = raw.trim();
    if (!candidate) continue;

    const bracketMatch = candidate.match(BRACKET_PATTERN);
    if (bracketMatch) {
      const constraintName = bracketMatch[1].trim();
      const itemsStr = bracketMatch[2].trim();
      const items =
        itemsStr.length === 0
          ? []
          : itemsStr
              .split(',')
              .map((item) => item.trim())
              .filter(Boolean);
      result.push({ constraintName, items });
    } else {
      const constraintName = candidate;
      result.push({ constraintName, items: [] });
    }
  }

  return result;
}

/**
 * Evaluate CONSTRAINT_ITEM_NOT_ON_CHECK conditions for a constraint against
 * the current cart item names.
 *
 * Returns true if none of the referenced items are present in the cart,
 * false if any are found.
 */
export function evaluateItemNotOnCheckConstraint(
  constraint: IConstraint | undefined,
  brandMenuConstraints: Record<string, IConstraint>,
  cartItemNames: string[]
): boolean {
  // Match Python behavior:
  // - missing constraint or menu => treat as passing (true)
  // - missing/empty cart => treat as failing (false)
  const conditions = (constraint as any)?.conditions as string[] | undefined;
  if (!constraint || !conditions || !brandMenuConstraints) {
    return true;
  }

  if (!cartItemNames || cartItemNames.length === 0) {
    return false;
  }

  const cartItems = cartItemNames.map((name) => name.toLowerCase());

  for (const rawCondition of conditions as (string | unknown)[]) {
    if (typeof rawCondition !== 'string') continue;

    const sepIndex = rawCondition.indexOf(':');
    if (sepIndex === -1) continue;

    const prefix = rawCondition.slice(0, sepIndex).trim();
    const conditionValue = rawCondition.slice(sepIndex + 1).trim();

    if (prefix.toUpperCase() !== CONSTRAINT_ITEM_NOT_ON_CHECK) continue;

    const andCandidates = parseItemNotOnCheckConditions(conditionValue);

    for (const { constraintName, items } of andCandidates) {
      const checkConstraint = brandMenuConstraints[constraintName];

      // If constraint not found and no explicit items, treat constraint name itself as item to check
      if (!checkConstraint && (!items || items.length === 0)) {
        const constraintNameLower = constraintName.toLowerCase();
        if (GENERIC_CONSTRAINT_OPTIONS.includes(constraintNameLower)) continue;

        // Normalize constraint name: replace underscores with spaces for matching
        const normalizedConstraintName = constraintNameLower.replace(/_/g, ' ');

        const foundInCart = cartItems.some(
          (cartName) =>
            cartName.includes(normalizedConstraintName) ||
            cartName.includes(constraintNameLower)
        );

        if (foundInCart) {
          return false;
        }
        continue;
      }

      if (!checkConstraint) continue;

      const itemsToCheck =
        items && items.length > 0 ? items : checkConstraint.child_items || [];

      for (const rawItem of itemsToCheck) {
        const itemName = rawItem.toLowerCase();

        // Skip generic "decline/yes/no" options
        if (GENERIC_CONSTRAINT_OPTIONS.includes(itemName)) continue;

        const foundInCart = cartItems.some((cartName) =>
          cartName.includes(itemName)
        );

        if (foundInCart) {
          return false;
        }
      }
    }
  }

  return true;
}

/**
 * Finds intent card from DEFAULT_INTENT_CARDS by matching BM phrase name to title (case-insensitive)
 */
function findIntentCardByTitle(phraseName: string): IntentCard | null {
  const normalizedPhraseName = phraseName.toLowerCase();

  return (
    DEFAULT_INTENT_CARDS.find(
      (card) => card.title.toLowerCase() === normalizedPhraseName
    ) || null
  );
}

/**
 * Builds INTENT_CARDS from conversational_reply utterances (English only)
 * Returns undefined if no valid cards can be built, otherwise returns array of IntentCard
 */
export function buildIntentCardsFromBM(
  utterances: IUtterance[] | undefined
): IntentCard[] | undefined {
  // If no utterances, return undefined (components handle fallback)
  if (!utterances || !Array.isArray(utterances) || utterances.length === 0) {
    return undefined;
  }

  const intentCards: IntentCard[] = [];

  // Use only conversational_reply category for intent messages, English language
  const englishConversationalUtterances = utterances.filter(
    (utterance) =>
      utterance.language === 'en' &&
      utterance.category === HITL_INTENT_PHRASE_CATEGORY
  );

  logger.debug({
    message:
      'buildIntentCardsFromBM: Building intent cards from utterances data',
    totalUtterances: utterances.length,
    englishConversationalUtterancesCount:
      englishConversationalUtterances.length,
  });

  englishConversationalUtterances.forEach((utterance) => {
    const matchDefaultCard = findIntentCardByTitle(utterance.name);
    if (!matchDefaultCard) {
      logger.debug({
        message:
          'buildIntentCardsFromBM: Skipping utterance - no matching title found for utterance name',
        utteranceName: utterance.name,
      });
      return;
    }

    const messages = Array.isArray(utterance.phrases) ? utterance.phrases : [];

    if (messages.length === 0) {
      return;
    }

    intentCards.push({
      ...matchDefaultCard,
      messages,
    });
  });

  // If not enough intent cards were built from BM data, return undefined (components handle fallback)
  if (intentCards.length < DEFAULT_INTENT_CARDS.length) {
    logger.debug({
      message:
        'buildIntentCardsFromBM: Not enough intent cards built from BM data, returning undefined',
      intentCardsCount: intentCards.length,
      defaultCardsCount: DEFAULT_INTENT_CARDS.length,
    });
    return undefined;
  }

  // Sort intent cards to match the order of DEFAULT_INTENT_CARDS
  const defaultOrder = DEFAULT_INTENT_CARDS.map((card) => card.intent);
  intentCards.sort((a, b) => {
    const indexA = defaultOrder.indexOf(a.intent);
    const indexB = defaultOrder.indexOf(b.intent);
    return (indexA === -1 ? 999 : indexA) - (indexB === -1 ? 999 : indexB);
  });

  logger.debug({
    message:
      'buildIntentCardsFromBM: Successfully built intent cards from BM data',
    intentCardsCount: intentCards.length,
  });

  return intentCards;
}

/**
 * Extracts "Dismiss & Readback" phrases from BM utterances (English,
 * conversational_reply category). Returns undefined when none are found
 * so callers can fall back to the hardcoded default.
 */
export function getDismissReadbackPhrasesFromBM(
  utterances: IUtterance[] | undefined
): string[] | undefined {
  if (!utterances || !Array.isArray(utterances) || utterances.length === 0) {
    return undefined;
  }

  const match = utterances.find(
    (u) =>
      u.language === 'en' &&
      u.category === HITL_INTENT_PHRASE_CATEGORY &&
      u.name.toLowerCase() === DISMISS_READBACK_UTTERANCE_NAME.toLowerCase()
  );

  if (!match || !Array.isArray(match.phrases) || match.phrases.length === 0) {
    return undefined;
  }

  return match.phrases;
}
